/**
 * One place that decides where owed breaks go, so every strategy agrees.
 *
 * Guards on the same post must not all break at once. Each guard short of a
 * break is given a slot in a fixed order (by name), stepping around breaks
 * already rostered for colleagues and the slots planned for colleagues earlier
 * in that order. A reassignment or trim that has to add a break asks here too,
 * so its break lands clear of the ones suggested for the breaches beside it.
 */

import { planBreakWindow } from '../breakPlanner';
import {
  buildGuardShifts,
  numberDetail,
  overlaps,
  requiredBreakMinutes,
} from '../rosterModel';
import type {
  GuardShift,
  RemediationContext,
  RosterSnapshot,
  TimeWindow,
} from '../types';

export type BreakPlanningContext = Pick<RemediationContext, 'snapshot' | 'rule' | 'violations'>;

const BREAK_RULE = 'break_allowance';

const toWindow = (shift: GuardShift): TimeWindow => ({ startMinute: shift.startMinute, endMinute: shift.endMinute });

const ownBreakWindows = (shift: GuardShift): TimeWindow[] => (
  shift.breaks.map((breakSlot) => ({ startMinute: breakSlot.start_minute, endMinute: breakSlot.end_minute }))
);

/** Breaks colleagues on the same duties already take during the shift. */
export function colleagueBreakWindows(snapshot: RosterSnapshot, shift: GuardShift): TimeWindow[] {
  const colleagueIds = new Set<string>();
  shift.duties.forEach((duty) => duty.assignments.forEach((assignment) => {
    if (assignment.guard_id !== shift.guardId) colleagueIds.add(assignment.guard_id);
  }));

  return snapshot.breakSlots
    .filter((breakSlot) => breakSlot.assignments.some((assignment) => colleagueIds.has(assignment.guard_id)))
    .map((breakSlot) => ({ startMinute: breakSlot.start_minute, endMinute: breakSlot.end_minute }))
    .filter((window) => overlaps(window, toWindow(shift)));
}

interface Sibling {
  shift: GuardShift;
  requiredMinutes: number | null;
}

const comesBefore = (a: GuardShift, b: GuardShift): boolean => (
  a.guardName.localeCompare(b.guardName) < 0
  || (a.guardName === b.guardName && a.guardId.localeCompare(b.guardId) < 0)
);

/** Colleagues sharing a duty with this shift who are also short of a break. */
function shortOfBreakSiblings(shift: GuardShift, context: BreakPlanningContext): Sibling[] {
  const dutyIds = new Set(shift.duties.map((duty) => duty.id));
  const siblings: Sibling[] = [];

  context.violations.forEach((violation) => {
    if (violation.rule_code !== BREAK_RULE || !violation.guard_id || violation.guard_id === shift.guardId) return;
    const siblingShift = buildGuardShifts(context.snapshot, violation.guard_id)
      .find((candidate) => candidate.duties.some((duty) => dutyIds.has(duty.id)));
    if (siblingShift) {
      siblings.push({ shift: siblingShift, requiredMinutes: numberDetail(violation, 'required_break_minutes') });
    }
  });

  return siblings;
}

export interface OwedBreakPlan {
  requiredMinutes: number;
  owedMinutes: number;
  /** One continuous break in place of the shorter ones rostered; null when there is none to replace. */
  replacement: TimeWindow | null;
  /** A separate break for just the minutes still owed. */
  topUp: TimeWindow | null;
}

/**
 * The break this shift is owed and where it should go. `requiredMinutes` comes
 * from the breach when there is one, otherwise from the rule's tiers.
 */
export function planOwedBreak(
  shift: GuardShift,
  context: BreakPlanningContext,
  requiredMinutes: number | null = null,
): OwedBreakPlan {
  const required = requiredMinutes ?? requiredBreakMinutes(context.rule, shift.dutyMinutes);
  const owed = required - shift.breakMinutes;
  if (owed <= 0) return { requiredMinutes: required, owedMinutes: 0, replacement: null, topUp: null };

  const avoid = [
    ...colleagueBreakWindows(context.snapshot, shift),
    ...siblingPlannedBreaks(shift, context, 'earlier'),
  ];

  return {
    requiredMinutes: required,
    owedMinutes: owed,
    replacement: shift.breaks.length > 0
      ? planBreakWindow({
          shift: toWindow(shift),
          durationMinutes: required,
          avoid,
          preferredStartMinute: shift.breaks[0].start_minute,
        })
      : null,
    topUp: planBreakWindow({
      shift: toWindow(shift),
      durationMinutes: owed,
      avoid: [...avoid, ...ownBreakWindows(shift)],
    }),
  };
}

/**
 * Break slots already spoken for by colleagues short of a break. `earlier`
 * is the standing order every guard's own plan follows; `all` is for a change
 * that reshapes the shift and so cannot rely on that order.
 */
export function siblingPlannedBreaks(
  shift: GuardShift,
  context: BreakPlanningContext,
  scope: 'earlier' | 'all',
): TimeWindow[] {
  return shortOfBreakSiblings(shift, context)
    .filter((sibling) => scope === 'all' || comesBefore(sibling.shift, shift))
    .flatMap((sibling) => {
      const plan = planOwedBreak(sibling.shift, context, sibling.requiredMinutes);
      const window = plan.replacement ?? plan.topUp;
      return window ? [window] : [];
    });
}
